import { useState, type ReactNode } from 'react';

import type { PendingOptIn, VerificationSettingChange, VerificationSettingsResponse } from '@clm/shared-types';

import {
  cosignFieldVerificationPolicy,
  cosignVerificationSettings,
  proposeVerificationSettings,
  selfCertifyVerificationSettings,
  withdrawVerificationSettings,
} from '../../api/verification-policy';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { PolicyProblemNotice } from './PolicyProblemNotice';
import {
  AUTO_APPROVAL_OFF_COPY,
  OPT_INS_SCOPE_ALL_COPY,
  PROPOSER_CANNOT_COSIGN_COPY,
  SETTER_CANNOT_COSIGN_COPY,
  SETTINGS_PREFILL,
  ageCopy,
  decidedByCopy,
  decisionsSpoken,
  grainCopy,
  policyProblemOf,
  settingsDraftProblem,
  settingsLine,
  type PolicyProblem,
  type SettingsDraft,
} from './shared';

/**
 * The settings card of the Auto-approval page — CLM-108 (D-2.1-7, R-108-9,
 * R-108-10, R-108-11, R-108-12, R-108-24). Shown to both roles.
 *
 * ## Two people, always
 *
 * An Admin proposes the threshold, the sample rate and the stale window
 * together, as one change row (pre-filled 0.900 / 0.200 / 90 — the 0.900 is
 * decided, the other two are R-108-9's defaults). Nothing takes effect until a
 * **different** Legal holder co-signs it; until the first co-sign the card says
 * auto-approval is off, because there is no seed row (R-108-10). The proposer's
 * own co-sign buttons are disabled with the reason — a dual-role holder
 * included — and the API refuses it anyway (`409 cosigner_is_proposer`), and so
 * does the database (UPN **and** object id). `caller_is_proposer` is the
 * server's comparison; the page never sees an object id.
 *
 * Co-signing is two clicks: the confirm repeats the values being attested to,
 * and the request carries them as `seen`, so a change under the co-signer is
 * `409 stale_view` — they attest to what they read, never to figures they were
 * not shown.
 *
 * ## The opt-ins awaiting co-sign (R-108-24)
 *
 * A per-field opt-in below the threshold is a threshold for one field, so it
 * takes the same co-sign. Each carries the basis it was taken on — org-wide
 * counts, so the list is computed for a Scope-All caller only and is `null`
 * otherwise (R-108-12), which the card says rather than showing an empty list.
 *
 * ## What is shown after a write
 *
 * Every figure is the API's; every write re-reads the page (`onChanged`), so
 * what is shown afterwards is what the database holds. The outcome of the last
 * write — its receipt, or the refusal in this page's words — is held here, on
 * the card, so the re-read that a refusal like `not_pending` asks for cannot
 * unmount the very notice explaining it.
 */

export interface LegalView {
  readonly caller_is_proposer: boolean;
  readonly impact: VerificationSettingsResponse['impact'];
  readonly pending_opt_ins: VerificationSettingsResponse['pending_opt_ins'];
}

/** The last write's outcome, on the card. */
type Outcome = { readonly kind: 'done'; readonly text: string } | { readonly kind: 'problem'; readonly problem: PolicyProblem };

/** What a write hands back to the card. */
interface WriteHooks {
  readonly onOutcome: (outcome: Outcome | null) => void;
  readonly onChanged: () => void;
}

/** Run one write: clear the last outcome, record the new one, re-read on success or on a refusal that says the state moved. */
async function runWrite(hooks: WriteHooks, action: () => Promise<string>): Promise<boolean> {
  hooks.onOutcome(null);
  try {
    const text = await action();
    hooks.onOutcome({ kind: 'done', text });
    hooks.onChanged();
    return true;
  } catch (error) {
    const problem = policyProblemOf(error);
    hooks.onOutcome({ kind: 'problem', problem });
    if (problem.refusal?.reload === true) hooks.onChanged();
    return false;
  }
}

function when(instant: string | null): ReactNode {
  return instant === null ? '—' : <time dateTime={instant}>{instant}</time>;
}

const HISTORY_COLUMNS: readonly DataTableColumn<VerificationSettingChange>[] = [
  { id: 'change', header: 'Change', cell: (row) => <code>{row.setting_change_id}</code> },
  { id: 'status', header: 'Status', cell: (row) => <span className="tag">{row.status}</span> },
  { id: 'revision', header: 'Revision', cell: (row) => (row.setting_revision === null ? '—' : `r${String(row.setting_revision)}`) },
  { id: 'values', header: 'Values', cell: (row) => settingsLine(row) },
  {
    id: 'proposed',
    header: 'Proposed',
    cell: (row) => (
      <>
        {row.proposed_by_upn} · {when(row.proposed_utc)}
      </>
    ),
  },
  {
    id: 'decided',
    header: 'Decided',
    cell: (row) =>
      row.decided_by_upn === null ? (
        <span className="mut">—</span>
      ) : row.is_self_certified ? (
        /* CLM-127, D-127-1 — the only branch that changes this column's existing words: a
         * self-certified row is never shown as the bare UPN the normal case always has been. */
        <>
          {decidedByCopy(row.decided_by_upn, true)} · {when(row.decided_utc)}
        </>
      ) : (
        <>
          {row.decided_by_upn} · {when(row.decided_utc)}
        </>
      ),
  },
  {
    id: 'notes',
    header: 'Notes',
    cell: (row) => [row.proposal_note, row.decision_note].filter((note): note is string => note !== null).join(' — '),
  },
];

export function SettingsCard({
  card,
  generatedUtc,
  isAdmin,
  legal,
  selfCertifyEnabled,
  onChanged,
}: {
  readonly card: {
    readonly current: VerificationSettingChange | null;
    readonly pending: VerificationSettingChange | null;
    readonly history: readonly VerificationSettingChange[];
  };
  readonly generatedUtc: string;
  /** The `verification-policy` read answered: the caller holds `admin:configure`. */
  readonly isAdmin: boolean;
  /** The `verification-settings` read answered: the caller holds Legal's co-sign action. */
  readonly legal: LegalView | null;
  /**
   * CLM-127, D-127-1 — `CLM_API_VERIFICATION_POLICY_ALLOW_SELF_CERTIFY`, as the
   * server read it (`self_certify_enabled` on the GET response). Offers the
   * self-certify button only when true; the server refuses the action either way.
   */
  readonly selfCertifyEnabled: boolean;
  readonly onChanged: () => void;
}) {
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const hooks: WriteHooks = { onOutcome: setOutcome, onChanged };

  return (
    <section className="card" aria-labelledby="vp-settings-heading" data-testid="vp-settings">
      <h3 id="vp-settings-heading">Threshold, sample and stale window</h3>

      {card.current === null ? (
        <p className="banner up" role="status" data-testid="vp-off">
          {AUTO_APPROVAL_OFF_COPY}
        </p>
      ) : (
        <div data-testid="vp-current">
          <p>
            <strong>In force — revision r{String(card.current.setting_revision ?? 0)}:</strong> {settingsLine(card.current)}.
          </p>
          <p className="mut">
            Proposed by {card.current.proposed_by_upn};{' '}
            {card.current.decided_by_upn === null ? '—' : decidedByCopy(card.current.decided_by_upn, card.current.is_self_certified)}; effective{' '}
            {when(card.current.decided_utc)}.
          </p>
        </div>
      )}

      {card.pending === null ? null : (
        <PendingChange
          key={card.pending.setting_change_id}
          pending={card.pending}
          current={card.current}
          generatedUtc={generatedUtc}
          isAdmin={isAdmin}
          legal={legal}
          hooks={hooks}
        />
      )}

      {isAdmin && card.pending === null ? (
        <ProposeForm current={card.current} hooks={hooks} selfCertifyEnabled={selfCertifyEnabled} />
      ) : null}
      {isAdmin && card.pending !== null ? (
        <p className="mut">One change can await a co-sign at a time; withdraw it to propose different values.</p>
      ) : null}

      <div aria-live="polite" data-testid="vp-settings-outcome">
        {outcome?.kind === 'done' ? (
          <p className="banner up" role="status">
            {outcome.text}
          </p>
        ) : null}
        {outcome?.kind === 'problem' ? <PolicyProblemNotice problem={outcome.problem} /> : null}
      </div>

      {legal === null ? null : <PendingOptIns optIns={legal.pending_opt_ins} hooks={hooks} />}

      <h4>History</h4>
      <DataTable
        caption="Settings changes, newest first"
        columns={HISTORY_COLUMNS}
        rows={card.history}
        rowKey={(row) => row.setting_change_id}
        empty={<p className="mut">No change has been proposed yet.</p>}
      />
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* The pending change                                                          */
/* -------------------------------------------------------------------------- */

function PendingChange({
  pending,
  current,
  generatedUtc,
  isAdmin,
  legal,
  hooks,
}: {
  readonly pending: VerificationSettingChange;
  readonly current: VerificationSettingChange | null;
  readonly generatedUtc: string;
  readonly isAdmin: boolean;
  readonly legal: LegalView | null;
  readonly hooks: WriteHooks;
}) {
  const [step, setStep] = useState<'idle' | 'confirm-cosign' | 'decline' | 'withdraw'>('idle');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const isProposer = legal?.caller_is_proposer === true;

  async function run(action: () => Promise<string>) {
    setBusy(true);
    const ok = await runWrite(hooks, action);
    setBusy(false);
    if (ok) {
      setStep('idle');
      setNote('');
    }
  }

  function cosign(decision: 'cosign' | 'decline') {
    const trimmed = note.trim();
    if (decision === 'decline' && trimmed === '') {
      setNoteError('A decline needs a reason — the Admin who proposed this reads it.');
      return;
    }
    setNoteError(null);
    void run(async () => {
      const response = await cosignVerificationSettings({
        setting_change_id: pending.setting_change_id,
        decision,
        // Exactly what this card showed: a change under the co-signer is 409 stale_view.
        seen: { threshold: pending.threshold, sample_rate: pending.sample_rate, stale_after_days: pending.stale_after_days },
        ...(trimmed === '' ? {} : { decision_note: trimmed }),
      });
      return response.change.status === 'effective'
        ? `Co-signed — revision r${String(response.change.setting_revision ?? 0)} is in force (audit row ${response.audit.audit_id}).`
        : `Declined (audit row ${response.audit.audit_id}). The values in force are unchanged.`;
    });
  }

  function withdraw() {
    const trimmed = note.trim();
    void run(async () => {
      const response = await withdrawVerificationSettings({
        setting_change_id: pending.setting_change_id,
        ...(trimmed === '' ? {} : { note: trimmed }),
      });
      return `Withdrawn (audit row ${response.audit.audit_id}). The values in force are unchanged.`;
    });
  }

  const rows: readonly { readonly label: string; readonly now: string; readonly proposed: string }[] = [
    { label: 'Threshold', now: current?.threshold ?? 'off', proposed: pending.threshold },
    { label: 'Sample rate', now: current?.sample_rate ?? '—', proposed: pending.sample_rate },
    {
      label: 'Stale window',
      now: current === null ? '—' : `${String(current.stale_after_days)} days`,
      proposed: `${String(pending.stale_after_days)} days`,
    },
  ];

  return (
    <div className="vcard" data-testid="vp-pending" role="group" aria-label="Pending settings change">
      <h4>Pending change — awaiting a Legal co-sign</h4>
      <table>
        <caption className="vh">Pending settings against the values in force</caption>
        <thead>
          <tr>
            <th scope="col">Setting</th>
            <th scope="col">In force</th>
            <th scope="col">Proposed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              <td>{row.now}</td>
              <td>{row.proposed}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mut">
        Proposed by {pending.proposed_by_upn}, {ageCopy(pending.proposed_utc, generatedUtc)} ({when(pending.proposed_utc)}):{' '}
        {pending.proposal_note}
      </p>
      {legal?.impact == null ? null : (
        <p className="mut" data-testid="vp-impact">
          {String(legal.impact.fields_auto_now)} field{legal.impact.fields_auto_now === 1 ? '' : 's'} auto-approve now;{' '}
          {legal.impact.fields_auto_at_pending === null
            ? 'no figure for these values'
            : `${String(legal.impact.fields_auto_at_pending)} would at these values`}
          .
        </p>
      )}

      <div className="vacts">
        {legal === null ? null : (
          <>
            <button
              type="button"
              className="btn pri"
              disabled={busy || isProposer}
              onClick={() => {
                setStep('confirm-cosign');
                setNoteError(null);
              }}
            >
              Co-sign
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy || isProposer}
              aria-expanded={step === 'decline'}
              onClick={() => {
                setStep(step === 'decline' ? 'idle' : 'decline');
                setNoteError(null);
              }}
            >
              Decline
            </button>
          </>
        )}
        {isAdmin ? (
          <button
            type="button"
            className="btn qt"
            disabled={busy}
            aria-expanded={step === 'withdraw'}
            onClick={() => {
              setStep(step === 'withdraw' ? 'idle' : 'withdraw');
            }}
          >
            Withdraw
          </button>
        ) : null}
      </div>
      {legal !== null && isProposer ? (
        <p className="mut" role="note" data-testid="vp-proposer-note">
          {PROPOSER_CANNOT_COSIGN_COPY}
        </p>
      ) : null}

      {step === 'confirm-cosign' ? (
        <div className="vform" role="group" aria-label="Confirm the co-sign">
          <p>
            You attest to <strong>{settingsLine(pending)}</strong>. They apply to every extraction after this co-sign; no
            field can auto-approve before it has 30 human decisions at its current grain.
          </p>
          <div className="field">
            <label htmlFor="vp-cosign-note">Note (optional)</label>
            <textarea
              id="vp-cosign-note"
              rows={2}
              maxLength={500}
              value={note}
              disabled={busy}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          <button type="button" className="btn pri" disabled={busy} onClick={() => cosign('cosign')}>
            {busy ? 'Recording…' : 'Confirm co-sign'}
          </button>
          <button type="button" className="btn qt" disabled={busy} onClick={() => setStep('idle')}>
            Cancel
          </button>
        </div>
      ) : null}

      {step === 'decline' ? (
        <div className="vform" role="group" aria-label="Decline the change">
          <div className="field">
            <label htmlFor="vp-decline-note">Why this change is declined</label>
            <textarea
              id="vp-decline-note"
              rows={2}
              maxLength={500}
              value={note}
              disabled={busy}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          <button type="button" className="btn pri" disabled={busy} onClick={() => cosign('decline')}>
            Record the decline
          </button>
        </div>
      ) : null}

      {step === 'withdraw' ? (
        <div className="vform" role="group" aria-label="Withdraw the change">
          <div className="field">
            <label htmlFor="vp-withdraw-note">Note (optional)</label>
            <textarea
              id="vp-withdraw-note"
              rows={2}
              maxLength={500}
              value={note}
              disabled={busy}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          <button type="button" className="btn pri" disabled={busy} onClick={withdraw}>
            Confirm withdraw
          </button>
        </div>
      ) : null}

      {noteError === null ? null : (
        <p className="ferr" role="alert">
          {noteError}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Propose — an Admin's, pre-filled                                            */
/* -------------------------------------------------------------------------- */

function ProposeForm({
  current,
  hooks,
  selfCertifyEnabled,
}: {
  readonly current: VerificationSettingChange | null;
  readonly hooks: WriteHooks;
  /** CLM-127, D-127-1 — offers the second, self-certify button only when the flag is on. */
  readonly selfCertifyEnabled: boolean;
}) {
  const [draft, setDraft] = useState<SettingsDraft>(() => ({
    threshold: current?.threshold ?? SETTINGS_PREFILL.threshold,
    sample_rate: current?.sample_rate ?? SETTINGS_PREFILL.sample_rate,
    stale_after_days: String(current?.stale_after_days ?? SETTINGS_PREFILL.stale_after_days),
    proposal_note: '',
  }));
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  async function propose() {
    const wrong = settingsDraftProblem(draft);
    if (wrong !== null) {
      setFieldError(wrong);
      return;
    }
    setFieldError(null);
    setBusy(true);
    await runWrite(hooks, async () => {
      const response = await proposeVerificationSettings({
        // The revision this page showed: another co-sign meanwhile is 409 settings_changed, never an overwrite.
        expected_setting_revision: current?.setting_revision ?? 0,
        threshold: draft.threshold,
        sample_rate: draft.sample_rate,
        stale_after_days: Number(draft.stale_after_days),
        proposal_note: draft.proposal_note.trim(),
      });
      return `Proposed (audit row ${response.audit.audit_id}). Nothing changes until a different Legal holder co-signs it.`;
    });
    setBusy(false);
  }

  /**
   * CLM-127, D-127-1 — the same draft, posted to the self-certify endpoint
   * instead: one request both proposes and decides it, under this Admin's own
   * identity. No second "pending" step exists for this path (R-127-18).
   */
  async function selfCertify() {
    const wrong = settingsDraftProblem(draft);
    if (wrong !== null) {
      setFieldError(wrong);
      return;
    }
    setFieldError(null);
    setBusy(true);
    await runWrite(hooks, async () => {
      const response = await selfCertifyVerificationSettings({
        expected_setting_revision: current?.setting_revision ?? 0,
        threshold: draft.threshold,
        sample_rate: draft.sample_rate,
        stale_after_days: Number(draft.stale_after_days),
        proposal_note: draft.proposal_note.trim(),
      });
      return (
        `Self-certified (audit row ${response.audit.audit_id}) — revision r${String(response.change.setting_revision ?? 0)} ` +
        'is in force. Not independently reviewed by Legal.'
      );
    });
    setBusy(false);
  }

  const set = (key: keyof SettingsDraft) => (value: string) => {
    setDraft((previous) => ({ ...previous, [key]: value }));
  };

  return (
    <div className="vform" role="group" aria-label="Propose settings" data-testid="vp-propose">
      <h4>{current === null ? 'Propose the first threshold' : 'Propose a change'}</h4>
      <p className="mut">
        All three values take effect only when a different Legal holder co-signs them (D-2.1-7). Lowering the sample
        rate or lengthening the window loosens the control just as lowering the threshold does.
      </p>
      <div className="field">
        <label htmlFor="vp-threshold">Threshold (three decimals)</label>
        <input
          id="vp-threshold"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={draft.threshold}
          disabled={busy}
          onChange={(event) => set('threshold')(event.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="vp-sample">Sample rate (three decimals)</label>
        <input
          id="vp-sample"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={draft.sample_rate}
          disabled={busy}
          onChange={(event) => set('sample_rate')(event.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="vp-window">Stale window (days)</label>
        <input
          id="vp-window"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={draft.stale_after_days}
          disabled={busy}
          onChange={(event) => set('stale_after_days')(event.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="vp-proposal-note">Why (Legal reads this before co-signing)</label>
        <textarea
          id="vp-proposal-note"
          rows={2}
          maxLength={500}
          value={draft.proposal_note}
          disabled={busy}
          onChange={(event) => set('proposal_note')(event.target.value)}
        />
      </div>
      <button type="button" className="btn pri" disabled={busy} onClick={() => void propose()}>
        {busy ? 'Proposing…' : 'Propose'}
      </button>
      {selfCertifyEnabled ? (
        <button
          type="button"
          className="btn qt"
          disabled={busy}
          data-testid="vp-self-certify-settings"
          onClick={() => void selfCertify()}
          title="Propose and decide it yourself in one step — not independently reviewed by Legal (CLM-127, D-127-1)."
        >
          {busy ? 'Self-certifying…' : 'Propose and self-certify'}
        </button>
      ) : null}
      {fieldError === null ? null : (
        <p className="ferr" role="alert">
          {fieldError}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Opt-ins awaiting co-sign — Legal's, Scope-All                               */
/* -------------------------------------------------------------------------- */

function PendingOptIns({ optIns, hooks }: { readonly optIns: readonly PendingOptIn[] | null; readonly hooks: WriteHooks }) {
  return (
    <div data-testid="vp-opt-ins">
      <h4>Opt-ins awaiting co-sign</h4>
      {optIns === null ? (
        <p className="banner up" role="status" data-testid="vp-opt-ins-withheld">
          {OPT_INS_SCOPE_ALL_COPY}
        </p>
      ) : optIns.length === 0 ? (
        <p className="mut">No per-field opt-in awaits a co-sign.</p>
      ) : (
        optIns.map((optIn) => (
          <OptInRow key={`${optIn.field_name}|${String(optIn.policy_revision)}`} optIn={optIn} hooks={hooks} />
        ))
      )}
    </div>
  );
}

function OptInRow({ optIn, hooks }: { readonly optIn: PendingOptIn; readonly hooks: WriteHooks }) {
  const [step, setStep] = useState<'idle' | 'confirm-cosign' | 'decline'>('idle');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const { basis } = optIn;

  async function decide(decision: 'cosign' | 'decline') {
    const trimmed = note.trim();
    if (decision === 'decline' && trimmed === '') {
      setNoteError('A decline needs a reason — the Admin who set this opt-in reads it.');
      return;
    }
    setNoteError(null);
    setBusy(true);
    await runWrite(hooks, async () => {
      const response = await cosignFieldVerificationPolicy({
        field_name: optIn.field_name,
        // The revision is the attestation: its basis is immutable, so this is exactly what was shown.
        policy_revision: optIn.policy_revision,
        decision,
        ...(trimmed === '' ? {} : { decision_note: trimmed }),
      });
      return decision === 'cosign'
        ? `Co-signed the opt-in for ${optIn.field_name} (audit row ${response.audit.audit_id}).`
        : `Declined the opt-in for ${optIn.field_name} (audit row ${response.audit.audit_id}); the field follows the threshold.`;
    });
    setBusy(false);
  }

  return (
    <div className="vcard" role="group" aria-label={`Opt-in for ${optIn.field_name}`} data-testid="vp-opt-in">
      <p>
        <code>{optIn.field_name}</code> · revision r{String(optIn.policy_revision)} · set by {optIn.changed_by_upn},{' '}
        {when(optIn.changed_utc)}
      </p>
      <p className="mut">
        Basis {basis.verified_count} / {basis.corrected_count} / {basis.rejected_count}
        <span className="vh"> ({decisionsSpoken(basis)})</span> · n = {basis.sample_size} · rate{' '}
        {basis.accuracy_rate ?? '—'} · {grainCopy(optIn.grain)}
      </p>
      <p>{optIn.change_note}</p>
      <div className="vacts">
        <button
          type="button"
          className="btn pri"
          disabled={busy || optIn.caller_is_setter}
          onClick={() => {
            setStep('confirm-cosign');
            setNoteError(null);
          }}
        >
          Co-sign
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy || optIn.caller_is_setter}
          aria-expanded={step === 'decline'}
          onClick={() => {
            setStep(step === 'decline' ? 'idle' : 'decline');
            setNoteError(null);
          }}
        >
          Decline
        </button>
      </div>
      {optIn.caller_is_setter ? (
        <p className="mut" role="note">
          {SETTER_CANNOT_COSIGN_COPY}
        </p>
      ) : null}
      {step === 'confirm-cosign' ? (
        <div className="vform" role="group" aria-label={`Confirm the co-sign for ${optIn.field_name}`}>
          <p>
            You agree that <code>{optIn.field_name}</code> may skip review at rate {basis.accuracy_rate ?? '—'} over{' '}
            {basis.sample_size} decisions, at this grain only.
          </p>
          <button type="button" className="btn pri" disabled={busy} onClick={() => void decide('cosign')}>
            {busy ? 'Recording…' : 'Confirm co-sign'}
          </button>
          <button type="button" className="btn qt" disabled={busy} onClick={() => setStep('idle')}>
            Cancel
          </button>
        </div>
      ) : null}
      {step === 'decline' ? (
        <div className="vform" role="group" aria-label={`Decline the opt-in for ${optIn.field_name}`}>
          <div className="field">
            <label htmlFor={`vp-optin-decline-${optIn.field_name}`}>Why this opt-in is declined</label>
            <textarea
              id={`vp-optin-decline-${optIn.field_name}`}
              rows={2}
              maxLength={500}
              value={note}
              disabled={busy}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          <button type="button" className="btn pri" disabled={busy} onClick={() => void decide('decline')}>
            Record the decline
          </button>
        </div>
      ) : null}
      {noteError === null ? null : (
        <p className="ferr" role="alert">
          {noteError}
        </p>
      )}
    </div>
  );
}
